import { useRouter } from "@tanstack/react-router"
import type { FormEvent, ReactNode } from "react"
import { useCallback, useEffect, useState } from "react"

import { accessStore } from "../../access/access-store"
import { useAccessState } from "../../access/use-access"
import { login } from "../../api/auth.api"
import { ApiProblem, isApiProblem, transportProblem } from "../../api/problem"
import { nextNeedsNoTenant, safeNextPath } from "../../app/navigate-next"
import { cx } from "../../lib/cx"
import { Button, TextField } from "../../ui"
import { enrollChallengeStore } from "../login/enroll-challenge"
import { mfaChallengeStore } from "../login/mfa-challenge"
import "../screen-tokens.css"
import styles from "./auth-screen.module.css"
import type { LoginOutcome } from "./login-state"
import {
  RATE_LIMIT_WAIT_SECONDS,
  TEXT_PHONE_NOT_SUPPORTED,
  credentialKind,
  loginKindOfProblem,
  loginView,
} from "./login-state"

/**
 * Вход в кабинет (1-Э1.1), семь состояний макета «Справочники и вход - П0».
 *
 * ЧТО РЕШАЕТ ЭТОТ ФАЙЛ, А ЧТО — login-state.ts. Здесь только запросы, поля и отсчёты;
 * КАКОЙ текст и КАКАЯ кнопка соответствуют отказу — там, отдельной чистой функцией
 * под проверками. Разделение не косметическое: семь состояний невозможно принять
 * глазами, если они заданы вперемешку с разметкой.
 *
 * ДВА РАСХОЖДЕНИЯ С МАКЕТОМ, КОТОРЫЕ НЕЛЬЗЯ БЫЛО ЗАКРЫТЬ ВЁРСТКОЙ.
 *
 * 1. Макет рисует первым полем «Телефон». Сервер принимает только почту
 *    (CabinetLoginRequest.email — packages/contracts/src/cabinet-auth.ts). Поле,
 *    подписанное «Телефон», по которому никто не может войти, — это не соответствие
 *    макету, а сломанный вход. Поэтому подпись называет оба способа, а номер, набранный
 *    в поле, ловится ДО отправки и объясняется словами.
 *
 * 2. «Войти по ссылке на почту» в API нет вовсе: есть только восстановление пароля
 *    (POST auth/password/reset). Действие показано ВЫКЛЮЧЕННЫМ С ПРИЧИНОЙ, а не
 *    спрятано, — по правилу §3.6 ТЗ: прячут то, чего не будет никогда. А состояние
 *    «Ссылка ушла» из макета живое: в него ведёт «Не помню пароль», у которого
 *    маршрут есть, и текст карточки для него верен слово в слово.
 */

/** Раз в секунду, пока идёт отсчёт. Свой, а не общий useMinute: здесь нужны секунды. */
function useCountdown(from: number, active: boolean): number {
  const [left, setLeft] = useState(from)

  useEffect(() => {
    if (!active) return
    setLeft(from)
    const timer = setInterval(() => {
      setLeft((value) => (value <= 1 ? 0 : value - 1))
    }, 1000)
    return () => {
      clearInterval(timer)
    }
  }, [from, active])

  return active ? left : 0
}

export function LoginScreen(props: { readonly next: string | undefined }): ReactNode {
  const router = useRouter()
  const state = useAccessState()

  const [credential, setCredential] = useState("")
  const [password, setPassword] = useState("")
  const [passwordShown, setPasswordShown] = useState(false)
  const [outcome, setOutcome] = useState<LoginOutcome>({ kind: "idle" })
  const [busy, setBusy] = useState(false)

  const kind = outcome.kind === "problem" ? loginKindOfProblem(outcome.problem) : null
  const retryIn = useCountdown(RATE_LIMIT_WAIT_SECONDS, kind === "many")

  const view = loginView({ outcome, retryIn })
  const stolenSession = state.kind === "anonymous" && state.reason === "reuse_detected"

  const fail = useCallback((error: unknown): void => {
    setOutcome({
      kind: "problem",
      problem: isApiProblem(error) ? error : transportProblem("unknown"),
    })
  }, [])

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      if (view.submitDimmed || busy) return

      // Номер отлавливается здесь, а не ответом сервера: 401 на верный номер отправляет
      // человека искать опечатку там, где её нет.
      if (credentialKind(credential) === "phone") {
        setOutcome({
          kind: "problem",
          problem: new ApiProblem({
            code: "invalid_credentials",
            status: 400,
            title: "Вход по номеру пока не работает",
            detail: TEXT_PHONE_NOT_SUPPORTED,
          }),
        })
        return
      }

      setBusy(true)
      try {
        const response = await login({ email: credential.trim(), password })

        if (response.status === "mfa_required") {
          // Второй фактор — отдельный адрес, а не окно поверх формы: у него своё
          // состояние, свой отказ и свой возврат назад (ТЗ §4).
          mfaChallengeStore.write(response.challenge)
          await router.navigate({ to: "/login/mfa", search: { next: props.next } })
          return
        }

        if (response.status === "mfa_enrollment_required") {
          // Роль требует фактор, а фактора нет. Это НЕ ошибка входа: пароль верный,
          // и человеку надо не чинить его, а завести фактор — на отдельном экране.
          enrollChallengeStore.write(response.enrollment)
          await router.navigate({ to: "/login/mfa/setup", search: { next: props.next } })
          return
        }

        const next = await accessStore.reload()
        if (next.kind === "tenant_required" && !nextNeedsNoTenant(props.next)) {
          await router.navigate({ to: "/choose-salon", search: { next: props.next } })
          return
        }
        router.history.push(safeNextPath(props.next, "/"))
      } catch (error) {
        // Пароль стирается ТОЛЬКО когда он и правда мог быть неверен. При «слишком
        // часто» (429 приложения или лимита nginx) попытка входа вообще не состоялась,
        // и стирать набранное значит наказывать человека за чужой перебор.
        if (isApiProblem(error) && loginKindOfProblem(error) === "bad") setPassword("")
        fail(error)
      } finally {
        setBusy(false)
      }
    },
    [busy, credential, fail, password, props.next, router, view.submitDimmed],
  )

  const detail = outcome.kind === "problem" ? outcome.problem.detail : null
  // Пояснение сервера показывается ВМЕСТО общего текста только там, где оно точнее:
  // «вход по номеру пока не работает» полезнее, чем «номер или пароль не подошли».
  const errorText =
    view.kind === "bad" && detail !== null && detail.length > 0 ? detail : view.errorText

  return (
    <div className={styles.page}>
      <form
        className={styles.card}
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className={styles.langRow}>
          {/*
            ПЕРЕКЛЮЧАТЕЛЬ ЯЗЫКА ВЫКЛЮЧЕН С ПРИЧИНОЙ, а не убран и не притворяется рабочим.
            Кнопка без обработчика — худший из трёх вариантов: человек нажимает, ничего
            не происходит, и он делает вывод, что сломан весь экран. Тексты входа сегодня
            написаны по-русски прямо в разметке, и переключение языка сменило бы подписи
            оболочки, оставив саму форму русской, — то есть показало бы поломку вместо
            перевода. Появится второй язык — появится и меню, здесь же.
          */}
          <button
            type="button"
            className={styles.lang}
            aria-disabled="true"
            title="Кабинет этапа П1 сдаётся по-русски: выбор языка появится вместе с переводом экрана входа. Язык интерфейса уже можно сменить в профиле."
            onClick={(event) => {
              event.preventDefault()
            }}
          >
            Русский ▾
          </button>
        </div>

        <div className={styles.brand}>planerius</div>
        <div className={styles.brandSub}>Кабинет салона</div>

        {stolenSession ? (
          <div className={styles.stolen} role="alert">
            <div className={styles.stolenTitle}>Сессия прервана</div>
            Кто-то предъявил ваш ключ доступа второй раз. Все сеансы завершены — войдите
            заново и смените пароль.
          </div>
        ) : null}

        {view.maintenanceText === null ? null : (
          <div className={styles.maintenance} role="status">
            {view.maintenanceText}
          </div>
        )}

        <TextField
          className={cx(styles.field, styles.phoneField, view.loginInvalid ? styles.invalid : undefined)}
          label="Телефон или почта"
          name="username"
          type="text"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="off"
          spellCheck={false}
          value={credential}
          onChange={(event) => {
            setCredential(event.target.value)
          }}
        />

        {view.showPassword ? (
          <div className={styles.passwordRow}>
            <TextField
              className={styles.field}
              label="Пароль"
              name="password"
              type={passwordShown ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
              }}
            />
            <Button
              variant="secondary"
              onClick={() => {
                setPasswordShown((shown) => !shown)
              }}
            >
              {passwordShown ? "Скрыть" : "Показать"}
            </Button>
          </div>
        ) : null}

        {errorText === null ? null : (
          <div className={styles.error} role="alert">
            {errorText}
          </div>
        )}

        <Button
          className={styles.submit}
          type="submit"
          variant="primary"
          block
          loading={busy}
          loadingLabel="Входим…"
          /*
           * Выключение причиной, а не disabled: над выключенной кнопкой браузер
           * не показывает подсказку, и объяснение «кабинет недоступен, дело не в пароле»
           * до человека уже не доберётся — ни мышью, ни с клавиатуры.
           */
          deniedReason={view.submitDimmed ? (view.errorText ?? view.maintenanceText ?? undefined) : undefined}
        >
          {view.submitLabel}
        </Button>

        <div className={styles.altActions}>
          <button
            type="button"
            className={styles.altAction}
            aria-disabled="true"
            title="Вход по ссылке на почту ещё не включён: такого маршрута нет в API. Войдите по паролю или нажмите «Не помню пароль»."
            onClick={(event) => {
              event.preventDefault()
            }}
          >
            Войти по ссылке на почту
          </button>
          <button
            type="button"
            className={styles.altAction}
            onClick={() => {
              /*
               * Ведём на ОТДЕЛЬНУЮ страницу и уносим уже введённый адрес: человек его
               * только что набрал, и просить набрать заново — способ потерять его на
               * опечатке. Раньше запрос уходил прямо отсюда, и состояние восстановления
               * путалось с состоянием входа.
               */
              const email = credential.trim()
              void router.navigate({
                to: "/forgot-password",
                search: email.includes("@") ? { email } : {},
              })
            }}
          >
            Не помню пароль
          </button>
        </div>

        <div className={styles.foot}>
          Сотрудника заводит салон приглашением. Открываете свой салон?{" "}
          <button
            type="button"
            className={styles.footLink}
            onClick={() => {
              void router.navigate({ to: "/create-salon" })
            }}
          >
            Создать салон
          </button>
        </div>
      </form>
    </div>
  )
}
